/**
 * ImportMasterProjectModal
 * -----------------------------------------------------
 * This modal allows a Super Admin to upload CSV/XLSX files 
 * containing Master Project data. It also displays the history 
 * of previously uploaded files along with their processing status.
 *
 * Features:
 *  - Shows upload history fetched from Firestore.
 *  - Allows downloading CSV template.
 *  - Uploads and processes new master project files.
 *  - Displays loader during upload.
 *
 * Author: Your Team
 */
import { FunctionComponent, useEffect, useState } from "react";
import styles from "./ImportModal.module.css";
import { fetchMasterProjectFile, uploadMasterProjectFile } from "../../../Services/contacts";
import moment from "moment";
import { connect, useDispatch } from "react-redux";
import { themeColors } from "../../theme";
import CSVGenerator from "./CSVGenerator";
import "../../../theme-vars.css";
import { useMediaQuery, useTheme } from "@mui/material";
import StyledSubmitButton from "../../Buttons/StyledSubmitButton";
import Modal, { Styles } from "react-modal";
import StyledCancelButton from "../../Buttons/StyledCancelButton";
import { Circles } from "react-loader-spinner";
import { projectHeader } from "../../../Values/masterHeader";

type props = {
    theme: any;
    open: boolean;
    close: () => void;
    history: any;
    usersList: any[];
    user: any;
};

const ImportMasterProjectModal: FunctionComponent<props> = ({
    theme,
    open,
    close,
    history,
    usersList,
    user,
}) => {
    const dispatcher = useDispatch();
    // Material UI theme & responsive breakpoints
    const themeMUI = useTheme();
    const isMobile = useMediaQuery(themeMUI.breakpoints.down("sm"));

    // Loader state
    const [load, setLoad] = useState(false);

    // Stores previously uploaded master project files
    const [uploadedFiles, setUploadedFiles] = useState<any[]>([]);

    /**
     * The CSV template must only include specific header fields.
     * We filter the master project header list and generate a CSV structure.
     */
    interface DataObject {
        [key: string]: any; // define your data object interface
    }

    const filtervalues = ["project_status", "project_name", "developer_name", "address", "rera_link", "walkthrough_link", "property_type", "property_stage", "status", "created_by"]

    // Filter only required headers
    const reqData = projectHeader.filter((obj: any) => filtervalues.includes(obj.label));

    // Add "Organization Name" and "Status" to the template header list
    const dbData = [{ "label": "organization_name", "value": "Organization Name" }, ...reqData, { "label": "status", "value": "Status" }];

    // Build a list of header labels only
    const headerData = dbData.map(item => item.value);

    // Wrap in an array to support CSVGenerator component
    const projectHeaderData: DataObject[] = [];
    projectHeaderData.push(headerData);

    /**
   * Fetch previously uploaded files on initial load.
   */
    useEffect(() => {
        const unsub = fetchMasterProjectFile(
            " ",
            (data: any) => setUploadedFiles(data),
            dispatcher
        );
        return () => {
            unsub();
        };
    }, []);

    /**
   * Upload master project CSV
   */
    const upload = async (file: any) => {
        setLoad(true);
        uploadMasterProjectFile(
            file,
            " ",
            dispatcher,
            (data: boolean) => setLoad(data),
            "Super Admin"
        );
    };

    /**
     * Table cell (td) styling based on theme mode
     */
    const tdStyles = {
        backgroundColor: theme ? themeColors.tdBackgroundColor : themeColors.tdBackgroundColorDark,
        color: theme ? themeColors.textPrimary : themeColors.textPrimaryDark,
    }

    /**
 * Table header (th) styling based on theme mode
 */
    const thStyles = {
        backgroundColor: theme ? themeColors.thBackgroundColor : themeColors.thBackgroundColorDark, color: theme ? themeColors.textPrimary : themeColors.textPrimaryDark
    }

    /**
    * Modal styling for react-modal
    */
    const modalStyle: Styles = {
        overlay: {
            backgroundColor: "rgba(0, 0, 0, 0.5)",
            zIndex: 1300,
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
        },
        content: {
            background: theme
                ? themeColors.cardBackgroundColor
                : themeColors.cardBackgroundColorDark,
            color: theme ? themeColors.textPrimary : themeColors.textPrimaryDark,
            borderRadius: 16,
            padding: isMobile ? "24px 12px" : "10px 20px",
            border: "none",
            boxShadow: "0 8px 32px rgba(0,0,0,0.18)",
            display: "flex",
            flexDirection: "column",
            justifyContent: "center",
            alignItems: "center",
            position: "relative",
            inset: "auto",
            margin: "auto",
        },
    };

    /** ------------------------------------------------------------------
   * Render Component
   * ------------------------------------------------------------------ */
    return (
        <>
            <Modal
                isOpen={open}
                onRequestClose={close}
                shouldCloseOnOverlayClick={true}
                shouldCloseOnEsc={true}
                style={modalStyle}
                ariaHideApp={false}
            >
                <div className={styles.child}>
                    {/* Modal Heading */}
                    <h5 style={{ fontFamily: "var(--font-family-main)", fontSize: "var(--font-size-large)", paddingTop: isMobile ? "unset" : "15px" }}>Import Data for "Project Form"</h5>

                    {/* Table showing past uploads */}
                    <div className={styles.tableBox}>
                        <table className={styles.table}>
                            <thead className={styles.head} style={{ fontFamily: "var(--font-family-main)", fontSize: "var(--font-size-small)" }} >
                                <tr>
                                    <th className={styles.th} style={thStyles}>Sno.</th>
                                    <th className={styles.th} style={thStyles}>Request Id</th>
                                    <th className={styles.th} style={thStyles}>Created At</th>
                                    <th className={styles.th} style={thStyles}>Status</th>
                                    <th className={styles.th} style={thStyles}>Upload Count</th>
                                    <th className={styles.th} style={thStyles}>Uploaded File</th>
                                    <th className={styles.th} style={thStyles}>Processed File</th>
                                </tr>
                            </thead>
                            <tbody>
                                {uploadedFiles.map((item: any, index: number) => (
                                    <tr key={index} className={styles.no}>
                                        <td className={styles.td} style={tdStyles}>{index + 1}</td>
                                        <td className={styles.td} style={tdStyles}>{item.request_id}</td>

                                        {/* Convert Firestore timestamp to readable date */}
                                        <td className={styles.td} style={tdStyles}>
                                            {moment(item.created_at.toDate()).format("MM/DD/YY HH:mm")}
                                        </td>
                                        <td className={styles.td} style={tdStyles}>{item.status}</td>
                                        <td className={styles.td} style={tdStyles}>{item.uploadCount}</td>
                                        <td className={styles.td} style={tdStyles}>
                                            <a href={item.fileUrl}>Uploaded File</a>
                                        </td>
                                        <td className={styles.td} style={tdStyles}>
                                            {item.responseURL !== "" && (
                                                <a href={item.responseURL}>Processed File</a>
                                            )}
                                        </td>
                                    </tr>
                                ))}
                            </tbody>
                        </table>
                    </div>

                    {/* Action Buttons */}
                    <div className={styles.selectBox}>
                        <div style={{ marginRight: 8 }}>

                            {/* Cancel */}
                            <StyledCancelButton onClick={close}>
                                Cancel
                            </StyledCancelButton>
                        </div>

                        {/* Download Template Button */}
                        <div style={{ marginRight: 8 }}>
                            <CSVGenerator data={projectHeaderData} /></div>
                        {/* Upload Section */}
                        <div>
                            <input
                                id="file-input"
                                className={styles.select}
                                type="file"
                                accept=".csv,.xlsx,.xls"
                                onChange={(e: any) => upload(e.target.files[0])}
                            />
                            <StyledSubmitButton
                                onClick={() => document.getElementById("file-input")?.click()}
                            >
                                {load ? (
                                    <span style={{ display: "flex", justifyContent: "center" }}>
                                        <Circles color="#fff" height={20} width={20} />Upload...
                                    </span>
                                ) : (
                                    <>Upload File</>
                                )}
                            </StyledSubmitButton>
                        </div>
                    </div>

                    {/* Footer Notes */}
                    <div style={{ textAlign: "center", marginTop: "1rem" }}>
                        <p style={{ fontFamily: "var(--font-family-main)", fontSize: "var(--font-size-small)" }}><i className="fas fa-info-circle"></i>  Only .csv file type is supported.</p>
                        <p style={{ marginTop: "-1rem", fontFamily: "var(--font-family-main)", fontSize: "var(--font-size-small)" }}><i className="fas fa-info-circle"></i>  Please ensure filling all the mandatory fields.</p>
                    </div>
                </div>
            </Modal>
        </>
    );
};

/** Map Redux store data to props */
const mapStateToProps = (state: any) => {
    return {
        theme: state.theme.isLightMode,
    };
};
export default connect(mapStateToProps)(ImportMasterProjectModal);
